// @ts-nocheck
/**
 * 大陸情報局 - メイン画面
 * ファンド投資のハブ画面
 */

import React, { useState, useMemo, useRef, useCallback } from 'react';
import { VERDE, getVerdeQuote, shouldShowHonestQuote } from '../data/verde';
import { FUND_TYPES } from '../data/funds';
import { getPortfolioSummary, getAvailableFunds } from '../systems/portfolioManager';
import { hasDebt, getDebtSummary } from '../systems/debtManager';
import { useIsMobile } from '../../hooks/useMediaQuery';
import RegionFunds from './RegionFunds';
import FundDetail from './FundDetail';
import Portfolio from './Portfolio';
import FXTrading from './FXTrading';
import DebtStatus from './DebtStatus';
import PawnShop from './PawnShop';
import { isPawnShopServerAvailable } from '../../../lib/usePawnShopServer';
import { syncInvestmentFromServer, isInvestmentServerAvailable } from '../../../lib/useInvestmentServer';

const InformationBureau = ({
  playerData,
  updatePlayerData,
  currentBattleNumber,
  allCards = [],
  marketIndex,
  marketState,
  onClose,
}) => {
  const isMobile = useIsMobile();

  // デフォルトタブ: ポジションがあればポートフォリオ、なければ地域
  const [activeTab, setActiveTab] = useState(() => {
    const investmentData = playerData.investmentData || {};
    const holdings = investmentData.holdings || [];
    const fxPositions = investmentData.fxPositions || [];
    return (holdings.length > 0 || fxPositions.length > 0) ? 'portfolio' : 'region';
  });
  const [selectedFund, setSelectedFund] = useState(null);
  const [showDebtStatus, setShowDebtStatus] = useState(false);
  const [showPawnShop, setShowPawnShop] = useState(false);
  const [verdeQuote, setVerdeQuote] = useState(() =>
    shouldShowHonestQuote() ? getVerdeQuote('honest') : getVerdeQuote('greeting')
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  // スクロール中のタップ誤検知を防ぐための状態
  const touchStartY = useRef(null);
  const lastTabSwitchTime = useRef(0);

  // タブ切り替えハンドラー（デバウンス付き）
  const handleTabSwitch = useCallback((tab) => {
    const now = Date.now();
    // 300ms以内の連続タップを無視（スクロール中の誤タップ対策）
    if (now - lastTabSwitchTime.current < 300) {
      return;
    }
    lastTabSwitchTime.current = now;
    setActiveTab(tab);
  }, []);

  // タッチ開始位置を記録
  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  // タッチ終了時にスクロールかタップかを判定
  const handleTouchEnd = useCallback((tab, e) => {
    if (touchStartY.current === null) return;
    const touchEndY = e.changedTouches[0].clientY;
    const distance = Math.abs(touchEndY - touchStartY.current);
    touchStartY.current = null;
    // 移動距離が15px未満ならタップとして認識
    if (distance < 15) {
      handleTabSwitch(tab);
    }
  }, [handleTabSwitch]);

  // サーバーから投資データを再同期
  const handleSyncFromServer = useCallback(async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    setSyncMessage(null);

    try {
      const result = await syncInvestmentFromServer();

      if (!result.success) {
        setSyncMessage({ type: 'error', text: `同期失敗: ${result.error}` });
        return;
      }

      // The shared economy listener has applied the full server snapshot.
      const localHoldings = result.holdings || [];
      const localFxPositions = result.fx_positions || [];
      setSyncMessage({
        type: 'success',
        text: `同期完了: ファンド${localHoldings.length}件, FX${localFxPositions.length}件`,
      });
    } catch (err) {
      setSyncMessage({ type: 'error', text: `同期エラー: ${err.message}` });
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, updatePlayerData]);

  const investmentData = playerData.investmentData || {};
  const summary = useMemo(
    () => getPortfolioSummary(investmentData, marketIndex, marketState),
    [investmentData, marketIndex, marketState]
  );

  // 借金状態
  const debtData = investmentData.debt;
  const isInDebt = hasDebt(debtData);
  const debtSummary = useMemo(
    () => isInDebt ? getDebtSummary(debtData, currentBattleNumber) : null,
    [debtData, currentBattleNumber, isInDebt]
  );

  // 質屋（サーバー接続時に表示）
  const showPawnShopButton = isPawnShopServerAvailable();

  // タブ定義
  const tabs = [
    { id: 'region', label: '地域', icon: '🗺️' },
    { id: 'meta', label: 'メタ', icon: '🏆' },
    { id: 'category', label: 'カテゴリ', icon: '📦' },
    { id: 'fx', label: 'FX', icon: '💱' },
    { id: 'portfolio', label: 'ポートフォリオ', icon: '📊' },
  ];

  // ファンド選択時
  const handleSelectFund = (fund) => {
    setSelectedFund(fund);
  };

  // ファンド詳細を閉じる
  const handleCloseFundDetail = () => {
    setSelectedFund(null);
  };

  // スタイル
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#1a1a2e',
      color: '#e0e0e0',
    },
    // モバイル用コンテナ（固定ヘッダー/フッター対応）
    containerMobile: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: '#1a1a2e',
      color: '#e0e0e0',
      paddingTop: '130px', // ヘッダー(シンプル行+NPC)の高さ分
      paddingBottom: '70px',
    },
    header: {
      padding: isMobile ? '12px' : '16px',
      borderBottom: '1px solid #333',
      backgroundColor: '#16213e',
    },
    // モバイル用固定ヘッダー
    headerMobile: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      padding: '10px 12px',
      borderBottom: '1px solid #333',
      backgroundColor: 'rgba(22, 33, 62, 0.98)',
      zIndex: 100,
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
    },
    title: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: 'bold',
      color: '#4fc3f7',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    closeButton: {
      padding: '8px 16px',
      backgroundColor: '#333',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    npcSection: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '12px',
      backgroundColor: 'rgba(79, 195, 247, 0.1)',
      borderRadius: '8px',
    },
    npcIcon: {
      fontSize: '32px',
    },
    npcContent: {
      flex: 1,
    },
    npcName: {
      fontWeight: 'bold',
      color: '#4fc3f7',
      marginBottom: '4px',
    },
    npcQuote: {
      fontSize: '14px',
      color: '#aaa',
      fontStyle: 'italic',
    },
    summaryBar: {
      display: isMobile ? 'grid' : 'flex',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'none',
      gap: isMobile ? '8px 12px' : '16px',
      padding: isMobile ? '10px 12px' : '12px 16px',
      backgroundColor: '#0f3460',
      borderBottom: '1px solid #333',
      flexWrap: 'wrap',
    },
    summaryItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: '2px',
      minWidth: isMobile ? 'auto' : '80px',
    },
    summaryLabel: {
      fontSize: isMobile ? '11px' : '11px',
      color: '#888',
      whiteSpace: 'nowrap',
    },
    summaryValue: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #333',
      backgroundColor: '#1a1a2e',
    },
    tab: {
      flex: 1,
      padding: isMobile ? '10px 8px' : '12px 16px',
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: '2px solid transparent',
      backgroundColor: 'transparent',
      color: '#888',
      cursor: 'pointer',
      fontSize: isMobile ? '12px' : '14px',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
    },
    tabActive: {
      color: '#4fc3f7',
      borderBottom: '2px solid #4fc3f7',
      backgroundColor: 'rgba(79, 195, 247, 0.1)',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px',
    },
    // フッタータブナビゲーション（モバイル用）
    footerTabNavigation: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'space-around',
      padding: '6px 4px',
      paddingBottom: 'calc(6px + env(safe-area-inset-bottom))',
      background: 'linear-gradient(180deg, rgba(22, 33, 62, 0.98) 0%, rgba(15, 15, 35, 0.99) 100%)',
      borderTop: '2px solid #4fc3f7',
      boxShadow: '0 -4px 20px rgba(79, 195, 247, 0.2)',
      zIndex: 100,
    },
    footerTab: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 2px',
      border: 'none',
      background: 'transparent',
      color: '#808090',
      fontSize: '10px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      borderRadius: '8px',
      margin: '0 2px',
      // タッチ操作の改善
      touchAction: 'manipulation', // ダブルタップズームを無効化
      WebkitTapHighlightColor: 'transparent', // タップハイライトを無効化
      userSelect: 'none', // テキスト選択を無効化
    },
    footerTabActive: {
      color: '#4fc3f7',
      background: 'rgba(79, 195, 247, 0.2)',
    },
    footerTabIcon: {
      fontSize: '20px',
      marginBottom: '2px',
    },
  };

  // 借金状態画面を表示中
  if (showDebtStatus) {
    return (
      <DebtStatus
        playerData={playerData}
        updatePlayerData={updatePlayerData}
        currentBattleNumber={currentBattleNumber}
        onClose={() => setShowDebtStatus(false)}
      />
    );
  }

  // 質屋画面を表示中
  if (showPawnShop) {
    return (
      <PawnShop
        playerData={playerData}
        updatePlayerData={updatePlayerData}
        allCards={allCards}
        onClose={() => setShowPawnShop(false)}
      />
    );
  }

  // ファンド詳細画面を表示中
  if (selectedFund) {
    return (
      <FundDetail
        fund={selectedFund}
        playerData={playerData}
        updatePlayerData={updatePlayerData}
        currentBattleNumber={currentBattleNumber}
        marketIndex={marketIndex}
        marketState={marketState}
        onClose={handleCloseFundDetail}
      />
    );
  }

  return (
    <div style={isMobile ? styles.containerMobile : styles.container}>
      {/* ヘッダー */}
      <div style={isMobile ? styles.headerMobile : styles.header}>
        {/* モバイル用シンプルヘッダー: ← | 大陸情報局 | 💰 xxxG */}
        {isMobile ? (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
          }}>
            <button
              style={{
                background: 'transparent',
                border: 'none',
                color: '#4fc3f7',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
              onClick={onClose}
            >
              ←
            </button>
            <div style={styles.title}>
              <span>🗺️</span>
              <span>大陸情報局</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              {isInvestmentServerAvailable() && (
                <button
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: isSyncing ? '#888' : '#4fc3f7',
                    fontSize: '16px',
                    cursor: isSyncing ? 'wait' : 'pointer',
                    padding: '4px',
                  }}
                  onClick={handleSyncFromServer}
                  disabled={isSyncing}
                  title="サーバーから再同期"
                >
                  {isSyncing ? '⏳' : '🔄'}
                </button>
              )}
              {showPawnShopButton && (
                <button
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ffd700',
                    fontSize: '16px',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                  onClick={() => setShowPawnShop(true)}
                >
                  🏪
                </button>
              )}
              <span style={{
                fontSize: '14px',
                color: '#ffd700',
                fontWeight: 'bold',
              }}>
                💰 {playerData.gold?.toLocaleString() || 0}G
              </span>
            </div>
          </div>
        ) : (
          /* PC用ヘッダー */
          <div style={styles.headerTop}>
            <div style={styles.title}>
              <span>🗺️</span>
              <span>大陸情報局</span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {isInvestmentServerAvailable() && (
                <button
                  style={{
                    ...styles.closeButton,
                    backgroundColor: isSyncing ? '#333' : '#1a3a5c',
                    color: isSyncing ? '#888' : '#4fc3f7',
                  }}
                  onClick={handleSyncFromServer}
                  disabled={isSyncing}
                >
                  {isSyncing ? '⏳ 同期中...' : '🔄 サーバー同期'}
                </button>
              )}
              {showPawnShopButton && (
                <button
                  style={{
                    ...styles.closeButton,
                    backgroundColor: '#4a3020',
                    color: '#ffd700',
                  }}
                  onClick={() => setShowPawnShop(true)}
                >
                  💰 質屋
                </button>
              )}
              <button style={styles.closeButton} onClick={onClose}>
                閉じる
              </button>
            </div>
          </div>
        )}

        {/* ヴェルデNPC */}
        <div style={styles.npcSection}>
          <div style={styles.npcIcon}>{VERDE.icon}</div>
          <div style={styles.npcContent}>
            <div style={styles.npcName}>{VERDE.name}</div>
            <div style={styles.npcQuote}>「{verdeQuote}」</div>
          </div>
        </div>

        {/* 同期メッセージ */}
        {syncMessage && (
          <div style={{
            padding: '8px 12px',
            backgroundColor: syncMessage.type === 'success' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
            color: syncMessage.type === 'success' ? '#4caf50' : '#f44336',
            fontSize: '13px',
            textAlign: 'center',
          }}>
            {syncMessage.text}
          </div>
        )}
      </div>

      {/* サマリーバー */}
      <div style={styles.summaryBar}>
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>投資総額</span>
          <span style={styles.summaryValue}>
            {summary.totalCost.toLocaleString()}G
          </span>
        </div>
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>評価額</span>
          <span style={styles.summaryValue}>
            {summary.totalValue.toLocaleString()}G
          </span>
        </div>
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>評価損益</span>
          <span
            style={{
              ...styles.summaryValue,
              color: summary.totalPnl >= 0 ? '#4caf50' : '#f44336',
            }}
          >
            {summary.totalPnl >= 0 ? '+' : ''}
            {summary.totalPnl.toLocaleString()}G
          </span>
        </div>
        <div style={styles.summaryItem}>
          <span style={styles.summaryLabel}>損益率</span>
          <span
            style={{
              ...styles.summaryValue,
              color: summary.totalPnlPercent >= 0 ? '#4caf50' : '#f44336',
            }}
          >
            {summary.totalPnlPercent >= 0 ? '+' : ''}
            {summary.totalPnlPercent.toFixed(1)}%
          </span>
        </div>

        {/* 借金インジケーター */}
        {isInDebt && (
          <button
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: isMobile ? '80px' : '100px',
              padding: '8px 12px',
              backgroundColor: 'rgba(255, 107, 107, 0.2)',
              border: '1px solid #ff6b6b',
              borderRadius: '4px',
              cursor: 'pointer',
              marginLeft: 'auto',
            }}
            onClick={() => setShowDebtStatus(true)}
          >
            <span style={{ fontSize: '11px', color: '#ff6b6b' }}>💸 借金</span>
            <span style={{
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 'bold',
              color: '#ff6b6b',
            }}>
              {debtSummary.amount.toLocaleString()}G
            </span>
            {debtSummary.isOverdue ? (
              <span style={{ fontSize: '10px', color: '#f44336' }}>⚠️ 期限切れ</span>
            ) : debtSummary.remainingDays <= 2 ? (
              <span style={{ fontSize: '10px', color: '#ff9800' }}>⚠️ あと{debtSummary.remainingDays}日</span>
            ) : null}
          </button>
        )}
      </div>

      {/* タブ（PC用） */}
      {!isMobile && (
        <div style={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* コンテンツ */}
      <div style={styles.content}>
        {activeTab === 'region' && (
          <RegionFunds
            investmentData={investmentData}
            onSelectFund={handleSelectFund}
            marketIndex={marketIndex}
            marketState={marketState}
          />
        )}
        {activeTab === 'meta' && (
          <RegionFunds
            investmentData={investmentData}
            onSelectFund={handleSelectFund}
            fundType={FUND_TYPES.META}
            title="大会メタファンド"
            description="大会結果に連動するファンドです"
            marketIndex={marketIndex}
            marketState={marketState}
          />
        )}
        {activeTab === 'category' && (
          <RegionFunds
            investmentData={investmentData}
            onSelectFund={handleSelectFund}
            fundType={FUND_TYPES.CATEGORY}
            title="カテゴリファンド"
            description="特定カテゴリの人気に連動するファンドです"
            marketIndex={marketIndex}
            marketState={marketState}
          />
        )}
        {activeTab === 'fx' && (
          <FXTrading
            playerData={playerData}
            updatePlayerData={updatePlayerData}
            currentBattleNumber={currentBattleNumber}
            marketIndex={marketIndex}
            marketState={marketState}
          />
        )}
        {activeTab === 'portfolio' && (
          <Portfolio
            playerData={playerData}
            updatePlayerData={updatePlayerData}
            currentBattleNumber={currentBattleNumber}
            onSelectFund={handleSelectFund}
            marketIndex={marketIndex}
            marketState={marketState}
          />
        )}
      </div>

      {/* フッタータブナビゲーション（モバイル用） */}
      {isMobile && (
        <div style={styles.footerTabNavigation}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              style={{
                ...styles.footerTab,
                ...(activeTab === tab.id ? styles.footerTabActive : {}),
              }}
              onTouchStart={handleTouchStart}
              onTouchEnd={(e) => handleTouchEnd(tab.id, e)}
              onClick={() => handleTabSwitch(tab.id)}
            >
              <span style={styles.footerTabIcon}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default InformationBureau;
